import React, { forwardRef, ForwardRefRenderFunction, useImperativeHandle, useState, useEffect, useCallback } from "react";
import axios from "axios";
import cn from "classnames";
import { v4 as uuid } from "uuid";
import * as Sentry from "@sentry/browser";

// Require Editor JS/CSS files.
import { faMicrochipAi, faSparkles } from "@fortawesome/pro-solid-svg-icons";
import "froala-editor/js/plugins/image.min.js";
import "froala-editor/js/plugins/link.min.js";
import "froala-editor/js/plugins/url.min.js";
import "froala-editor/js/plugins/colors.min.js";
import "froala-editor/js/plugins/lists.min.js";
import "froala-editor/js/plugins/quick_insert.min.js";
import "froala-editor/js/plugins/inline_style.min.js";
import "froala-editor/js/plugins/table.min.js";
import "froala-editor/js/plugins/paragraph_format.min.js";

import "froala-editor/css/plugins/quick_insert.min.css";

import "froala-editor/js/third_party/font_awesome.min.js";

import { FroalaEditorBase } from "./FroalaEditorBase";
import {
  baseConfigWithModifiers,
  buildGrammarCheckConfig,
  buildImagePluginConfig,
  FroalaConfigModifier,
  FroalaConfigModifiers
} from "./configs";
import SplitEditor, { setAiRequestInitialized } from "./SplitEditor";
import { IEditor } from "./interfaces";

import styles from "./FroalaEditor.module.scss";
import { getAskCaitButtonHtml } from "./askCaitButtonHtml";
import { sanitizeXML } from "../../../utils/xmlSanitizer";
import { MessageBar, MessageBarType } from "office-ui-fabric-react";

const defineAskCaitButton = () => {
  if (typeof window === "undefined" || !window.FroalaEditor) return;
  if (window.FroalaEditor.caitButtonDefined) return;
  window.FroalaEditor.caitButtonDefined = true;

  const FE = window.FroalaEditor;

  FE.ICON_TEMPLATES["fa-svg"] = getAskCaitButtonHtml({
    className: "[CLASS]",
    path: "[PATH]"
  }); ;

  FE.DefineIcon("askCait", {
    PATH: faSparkles.icon[4],
    CLASS: styles.askCaitIcon,
    template: "fa-svg"
  });

  FE.RegisterCommand("askCait", {
    icon: "askCait",
    focus: true,
    undo: true,
    refreshAfterCallback: true,
    callback: function() {
      this.toggleEditorSplit();
    }
  });
};

defineAskCaitButton();

function * groupToolbarButtons(toolbarButtons) {
  const groupOrder = ["moreRich", "moreText", "moreParagraph", "moreMisc", "caitTools"];

  for (const group of groupOrder) {
    if (toolbarButtons[group]) {
      for (const button of toolbarButtons[group].buttons) {
        yield button;
      }
      yield "|";
    }
  }
};

interface FroalaEditorProps {
  value: string
  onChange: (val: string) => void
  rtc?: boolean
  rtcDocId?: string
  rtcUsername?: string
  imageEnabled?: boolean
  linksEnabled?: boolean
  grammarCheck?: boolean
  objectReference?: string
  versionModel?: any
  versionKey?: string
  name?: string
  autoFocus?: boolean
  characterLimit?: number
  recommendedCharLimit?: number
  characterLimitAlgo?: (textContent: string, htmlContent: string) => number
  onCharLimitExceeded?: (exceeded: boolean) => void
  placeholder?: string
  noBreak?: boolean
  className?: string
  toolbarTitle?: string
  height?: number
  imageFieldName?: string
  unlimitedHeight?: boolean
  configModifiers?: FroalaConfigModifier[]
  AIEnabled?: boolean
  aiVars?: Record<string, string>
}

interface FroalaEditorHandle {
  setValue: (string) => void
  getValue: () => string | undefined
}

async function getBlobFromUrl(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return blob;
}

async function uploadToServer($img, objectReference, fieldName, objectParams) {
  const src = $img.attr("src");
  if (src && src?.startsWith("blob")) {
    objectParams.pendingOperations++;
    try {
      const formData = new FormData();
      const imageData = await getBlobFromUrl(src);
      formData.append("file", imageData);
      formData.append("object", objectReference);
      formData.append("field", fieldName);
      const response = await axios.post("/visual_editor_images", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      $img.attr("src", response.data.link);
    } catch (error) {
      // Log details to Sentry and console
      Sentry.captureException(error, {
        extra: {
          url: "/visual_editor_images",
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers,
          user: window.current_user, // if available
          objectReference,
          fieldName
        }
      });
    } finally {
      objectParams.pendingOperations--;
    }
  }
}

const FroalaEditor: ForwardRefRenderFunction<FroalaEditorHandle, FroalaEditorProps> = (
  {
    value,
    imageEnabled,
    linksEnabled,
    grammarCheck,
    objectReference,
    versionModel,
    versionKey,
    onChange,
    autoFocus,
    characterLimit,
    recommendedCharLimit,
    characterLimitAlgo,
    onCharLimitExceeded,
    placeholder,
    noBreak,
    className,
    toolbarTitle,
    height,
    unlimitedHeight,
    imageFieldName,
    configModifiers = [],
    AIEnabled,
    aiVars
  }: FroalaEditorProps, forwardedRef) => {
  const [editor, setEditor] = useState<IEditor>();
  const [editorSplit, setEditorSplit] = useState<boolean>(false);
  const [editorId, _] = useState(uuid());

  useEffect(() => {
    global.editorSplit ||= {};
    global.editorSplit[editorId] = editorSplit;
  }, [editorSplit]);

  useEffect(() => {
    if (editor) {
      // @ts-expect-error
      const $askCaitButton = editor.$box.find('[data-cmd="askCait"]');

      if (editorSplit) {
        if ($askCaitButton.length) {
          $askCaitButton.addClass("hidden");
        }
      } else {
        $askCaitButton.removeClass("hidden");
      }
    }
  }, [editorSplit, editor]);

  const toggleEditorSplit = () => {
    setAiRequestInitialized(global.editorSplit[editorId]);
    setEditorSplit(!global.editorSplit[editorId]);
  };
  const objectParams = { pendingOperations: 0 };

  useImperativeHandle(forwardedRef, () => ({
    setValue: (value) => {
      editor?.html.set(value);
    },
    getValue: () => {
      return editor?.html.get();
    }
  }));

  const baseConfig = baseConfigWithModifiers(configModifiers);

  const pluginsEnabled = [...baseConfig.pluginsEnabled];
  if (grammarCheck) pluginsEnabled.push("BeyondGrammarPlugin");
  if (characterLimit) pluginsEnabled.push("charCounter");
  if (linksEnabled) pluginsEnabled.push("link");
  if (imageEnabled) pluginsEnabled.push("image");
  pluginsEnabled.push("url");

  const imagePluginConfig = imageEnabled && buildImagePluginConfig(objectReference, imageFieldName);
  const grammarCheckConfig = grammarCheck && buildGrammarCheckConfig();
  const characterLimitConfig = { characterLimit, recommendedCharLimit, characterLimitAlgo, onCharLimitExceeded };

  const charCounterConfig = characterLimit && {
    charCounterCount: true
  };

  const baseEvents: Record<string, any> = {
    initialized: function() {
      // @ts-expect-error - 'this' in Froala's context is the editor instance
      setEditor(this);
      this.toggleEditorSplit = toggleEditorSplit;

      if (autoFocus) {
        this.events.focus(true);
      }
    }
  };

  const events: Record<string, any> = { ...baseEvents };
  if (imageEnabled) {
    events["image.removed"] = function($img) {
      const src = $img.attr("src");
      if (src && !src.startsWith("blob")) {
        axios.delete(src)
          .catch((e) => {
            console.error(e);
          });
      }
    };
    events["image.uploaded"] = function(response) {
      console.log("image.uploaded: response", response);
    };
    events["image.loaded"] = function($img) {
      uploadToServer($img, objectReference, imageFieldName, objectParams);
    };
    events.destroy = function() {
      // alert user if there are pending operations
      if (objectParams.pendingOperations > 0) {
        alert("There were pending image uploads. We recommend re-uploading.");
      }
    };
    events["image.error"] = function(error, response) {
      console.log("image.error: error|response", error, response);
    };
  }

  let { toolbarButtons } = baseConfig;
  if (imageEnabled) {
    toolbarButtons = {
      ...toolbarButtons,
      moreRich: {
        ...toolbarButtons.moreRich,
        buttons: [...toolbarButtons.moreRich.buttons, "insertImage"]
      }
    };
  }

  // Add "Ask CAIT" button when AIEnabled is true
  if (AIEnabled) {
    // Add the button to the toolbar
    toolbarButtons = {
      ...toolbarButtons,
      // Add a dedicated button group for CAIT
      caitTools: {
        buttons: ["askCait"],
        buttonsVisible: 1
      },
      // Keep the original moreRich group unchanged
      moreRich: {
        ...toolbarButtons.moreRich
      }
    };
  }
  toolbarButtons = Array.from(groupToolbarButtons(toolbarButtons));

  const inputHeight = unlimitedHeight ? {} : { height: height || 300 };

  const isInModal = configModifiers.includes(FroalaConfigModifiers.inModal);

  const config = {
    ...baseConfig,
    ...imagePluginConfig,
    ...charCounterConfig,
    ...grammarCheckConfig,
    ...inputHeight,
    ...characterLimitConfig,
    toolbarButtons,
    multiLine: !noBreak,
    pluginsEnabled,
    placeholderText: placeholder || "",
    events
  };

  const [removedCodes, setRemovedCodes] = useState<string[]>([]);

  const handleModelChange = (rawHtml: string) => {
    const { cleaned, removedCodes } = sanitizeXML(rawHtml);
    if (removedCodes.length) {
      setRemovedCodes(removedCodes);
    }
    onChange?.(cleaned);
  };

  return <>
    {removedCodes.length > 0 && (
        <MessageBar
            key={"n"}
            messageBarType={MessageBarType.error}
            isMultiline={false}
            dismissButtonAriaLabel="Close"
            overflowButtonAriaLabel="See more"
            onDismiss={() => setRemovedCodes([])}
        >
          Some unsupported characters were removed. These characters are not compatible with reports and have been automatically cleaned.
        </MessageBar>
    )}
    <div>
      <FroalaEditorBase
        model={value}
        config={config}
        onModelChange={handleModelChange}
        className={cn(className, { "in-modal": isInModal })}
        toolbarTitle={toolbarTitle}
        versionModel={versionModel}
        versionKey={versionKey}
        split={editorSplit && <SplitEditor aiVars={aiVars} editor={editor} onClose={() => setEditorSplit(false)} />}
      />
    </div>
  </>;
};

export default forwardRef(FroalaEditor);
